import type { Tour } from '@/types/product-tour';

// Define all product tours
export const tours: Tour[] = [
  {
    id: 'welcome-tour',
    name: 'Welcome to TimeTrack Pro',
    description: 'A quick 60-second tour of key features',
    trigger: 'first_login',
    priority: 100,
    isOptional: true,
    estimatedDuration: 60,
    segment: ['new_user'],
    steps: [
      {
        id: 'welcome',
        title: '👋 Welcome to TimeTrack Pro!',
        description: 'Let\'s take a quick tour of the key features. This will only take 60 seconds.',
        placement: 'center',
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'homepage',
        title: '📊 Your Homepage',
        description: 'This is your personal homepage. Here you\'ll see your timesheet summary, pending approvals, and key metrics at a glance.',
        target: '[data-tour="homepage"]',
        placement: 'bottom',
        spotlightPadding: 20,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'navigation',
        title: '🧭 Navigation',
        description: 'Use the sidebar to navigate between Timesheets, Approvals, Reports, and more. Everything you need is just a click away.',
        target: '[data-tour="sidebar"]',
        placement: 'right',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'quick-entry',
        title: '⚡ Quick Entry',
        description: 'Need to log time fast? Use the Quick Entry button to add timesheet entries in seconds.',
        target: '[data-tour="quick-entry"]',
        placement: 'bottom',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'help',
        title: '❓ Need Help?',
        description: 'Access help, documentation, and restart this tour anytime from the help menu in the header.',
        target: '[data-tour="help-menu"]',
        placement: 'bottom',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'complete',
        title: '🎉 You\'re All Set!',
        description: 'You\'re ready to start tracking time. Remember, you can always restart this tour from the help menu.',
        placement: 'center',
        showSkip: false,
        showPrevious: true,
      },
    ],
  },
  {
    id: 'timesheet-tour',
    name: 'Timesheet Features',
    description: 'Learn how to manage your timesheets effectively',
    trigger: 'feature_discovery',
    priority: 80,
    isOptional: true,
    estimatedDuration: 45,
    segment: ['new_user', 'employee'],
    steps: [
      {
        id: 'timesheet-grid',
        title: '📅 Timesheet Grid',
        description: 'View and edit your time entries for the week. Click any cell to add or modify hours.',
        target: '[data-tour="timesheet-grid"]',
        placement: 'top',
        spotlightPadding: 20,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'add-row',
        title: '➕ Add Projects',
        description: 'Click "Add Row" to add new projects to your timesheet. You can track time across multiple projects.',
        target: '[data-tour="add-row"]',
        placement: 'left',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'submit',
        title: '✅ Submit for Approval',
        description: 'When you\'re done, submit your timesheet for manager approval. You\'ll be notified once it\'s reviewed.',
        target: '[data-tour="submit-timesheet"]',
        placement: 'left',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
    ],
  },
  {
    id: 'manager-tour',
    name: 'Manager Homepage',
    description: 'Learn how to manage your team\'s timesheets',
    trigger: 'first_login',
    priority: 90,
    isOptional: true,
    estimatedDuration: 50,
    segment: ['manager', 'admin'],
    steps: [
      {
        id: 'manager-welcome',
        title: '👔 Manager Homepage',
        description: 'As a manager, you have access to team oversight and approval features.',
        placement: 'center',
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'approvals',
        title: '✅ Pending Approvals',
        description: 'Review and approve your team\'s timesheets here. You\'ll see all pending submissions that need your attention.',
        target: '[data-tour="approvals"]',
        placement: 'bottom',
        spotlightPadding: 20,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'team-reports',
        title: '📊 Team Reports',
        description: 'Access detailed reports on team utilization, project time, and more.',
        target: '[data-tour="reports"]',
        placement: 'right',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'team-members',
        title: '👥 Team Members',
        description: 'View and manage your team members, their schedules, and capacity.',
        target: '[data-tour="team-members"]',
        placement: 'right',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
    ],
  },
  {
    id: 'reports-tour',
    name: 'Reports & Analytics',
    description: 'Discover powerful reporting features',
    trigger: 'feature_discovery',
    priority: 60,
    isOptional: true,
    estimatedDuration: 40,
    segment: ['manager', 'admin', 'power_user'],
    steps: [
      {
        id: 'reports-overview',
        title: '📈 Reports Overview',
        description: 'Access comprehensive reports to analyze time tracking data across your organization.',
        target: '[data-tour="reports"]',
        placement: 'right',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: false,
      },
      {
        id: 'timesheet-reports',
        title: '📋 Timesheet Reports',
        description: 'View detailed timesheet data with filtering and export options.',
        target: '[data-tour="timesheet-reports"]',
        placement: 'right',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
      {
        id: 'utilization',
        title: '⚡ Utilization Reports',
        description: 'Track team utilization rates and identify capacity issues.',
        target: '[data-tour="utilization-reports"]',
        placement: 'right',
        spotlightPadding: 10,
        showSkip: true,
        showPrevious: true,
      },
    ],
  },
];

// Helper function to get tour by ID
export const getTourById = (tourId: string): Tour | undefined => {
  return tours.find(tour => tour.id === tourId);
};

// Helper function to get tours by segment
export const getToursBySegment = (segment: string): Tour[] => {
  return tours.filter(tour => 
    !tour.segment || tour.segment.includes(segment as any)
  ).sort((a, b) => b.priority - a.priority);
};

// Helper function to get tours by trigger
export const getToursByTrigger = (trigger: string): Tour[] => {
  return tours.filter(tour => tour.trigger === trigger)
    .sort((a, b) => b.priority - a.priority);
};
